/**
 * The match: what happens before a fight, and what stops one.
 *
 * Everything up to here answered "what does this blow do". Nothing answered
 * "and then what". A fight had no beginning, no end and no way back to the
 * start — a knocked-out fighter got up again and the two of them carried on
 * forever.
 *
 * Three phases, and they are the whole module:
 *
 *   intro     the fighters are on their marks and nobody may act
 *   fighting  the only phase in which the controls do anything
 *   over      somebody has been finished; the loser is in `dead`
 *
 * Pure. It holds no three.js and no renderer — a combatant is anything with a
 * position, a state machine and a health model, which is why a whole match can
 * be played out in a unit test.
 */

import CONFIG from './match.json';
import { decide, validateDecision } from './decision.js';

export class MatchConfigError extends Error {}

export function validateMatch(config = CONFIG) {
  for (const field of ['intro', 'celebrate', 'limit']) {
    if (!(config?.[field] > 0)) {
      throw new MatchConfigError(`match.${field} is ${JSON.stringify(config?.[field])}; want seconds`);
    }
  }
  // A limit shorter than the walk-on is a round that is over before anybody is
  // allowed to move.
  if (!(config.limit > config.intro)) {
    throw new MatchConfigError(`match.limit ${config.limit} is inside the intro of ${config.intro}`);
  }
  validateDecision(config.decision);
  return config;
}

validateMatch(CONFIG);

/** The shipped numbers. Overridable per match, which is what the tests do. */
export const MATCH = Object.freeze({ ...CONFIG });

/** How a round can end. A `draw` has no winner, which every reader must expect. */
export const VERDICTS = Object.freeze(['knockout', 'decision', 'draw']);

export const PHASES = Object.freeze(['intro', 'fighting', 'over']);

/**
 * `fighters` are the combatants; `stances` are the floor positions to put them
 * back on, in the same order. `fight` is watched for the blow that finishes
 * somebody — the knockout rules live in `fight.js`, and this module only reacts
 * to the verdict.
 */
export function createMatch({ fight, fighters = [], stances = [], ...overrides } = {}) {
  const config = { ...MATCH, ...overrides };
  let marks = stances;
  const listeners = new Set();

  let phase = 'intro';
  let elapsed = 0;
  /**
   * Whether something outside is holding the fighters on their marks.
   *
   * The intro is 1.2 seconds, which is long enough to see two people square up
   * and nowhere near long enough to read who they are. A walkout screen needs
   * the round to *wait*, so it says so here rather than by not calling
   * `update` — a caller that stops ticking the match stops ticking the clock
   * the celebrate timer runs on too, and the bug that produces is a round that
   * can never be left.
   */
  let held = false;
  let winner = null;
  let loser = null;
  let finishedBy = null;
  /** Which of the three ways it ended. Null until it has. */
  let verdict = null;
  /** Which bar decided it, when it went to the cards. */
  let decidedOn = null;

  const stop = fight?.onKnockout?.(({ winner: by, loser: to, hit }) => {
    // A second knockout in the same frame cannot rewrite the first. The fight
    // was already over when it landed.
    if (phase === 'over') return;
    winner = by;
    loser = to;
    finishedBy = hit?.strike ?? null;
    verdict = 'knockout';
    enter('over');
  });

  /**
   * The bell. Reads the bars and ends the round on them.
   *
   * The heat goes on *here* rather than in `fight.js`, which is where every
   * other win is scored, because a decision is not a blow — there is nothing
   * for `land()` to have landed. It is the one verdict the match raises itself.
   */
  function callIt() {
    const called = decide(fighters, config.decision);
    verdict = called.verdict;
    winner = called.winner;
    loser = called.loser;
    decidedOn = called.on;
    finishedBy = null;

    // A round that went the distance is worth something to the crowd, and less
    // than a finish. On a draw both of them wear it: "it went to the cards" is
    // the same complaint whoever the card went to.
    for (const fighter of fighters) {
      if (called.verdict === 'draw' || fighter.id === winner) {
        fighter.heat?.record?.('decision', { opponent: fighters.find((o) => o !== fighter)?.id ?? null });
      }
    }
    enter('over');
    return called;
  }

  function enter(next) {
    if (phase === next) return;
    const from = phase;
    phase = next;
    elapsed = 0;
    for (const listener of listeners) {
      listener({ from, to: next, winner, loser, finishedBy, verdict, on: decidedOn });
    }
  }

  /** Puts one fighter back to the state they start a round in. */
  function restore(fighter, stance) {
    fighter.strikes?.cancel?.();
    fighter.combo?.reset?.();
    fighter.momentum?.reset?.();
    // Heat goes back with the rest of it. A round is a fight, and the crowd
    // does not remember the last one — there is only ever one round.
    fighter.heat?.reset?.();
    fighter.consumeVulnerability?.();
    fighter.health?.reset?.();
    // Forced, not requested: `dead` is terminal and would refuse every door
    // out of itself, which is exactly what it is for.
    fighter.machine?.force?.('idle');
    if (stance && fighter.position) {
      fighter.position.x = stance[0];
      fighter.position.z = stance[1] ?? stance[2] ?? 0;
    }
  }

  return {
    config,

    /** Where the fighters stand at the start of a round. The arena decides. */
    get stances() {
      return marks;
    },
    setStances(next = []) {
      marks = next;
      return marks;
    },
    /**
     * Onto the marks, and nothing else: no phase, no listener, no health. For
     * an arena that changes **during** the intro — a card fought somewhere
     * else — where the round has already been reset and another reset would
     * fire the intro a second time and drop the hold the screens are keeping.
     */
    toMarks() {
      fighters.forEach((fighter, i) => {
        const stance = marks[i];
        if (!stance || !fighter.position) return;
        fighter.position.x = stance[0];
        fighter.position.z = stance[1] ?? stance[2] ?? 0;
      });
      return marks;
    },

    get phase() {
      return phase;
    },
    /** The only phase in which a control layer should be driving anybody. */
    get live() {
      return phase === 'fighting';
    },
    get over() {
      return phase === 'over';
    },
    get winner() {
      return winner;
    },
    get loser() {
      return loser;
    },
    /** Which move ended it, or null when nothing did. */
    get finishedBy() {
      return finishedBy;
    },
    /** `knockout`, `decision`, `draw`, or null while it is still going. */
    get verdict() {
      return verdict;
    },
    /** Which bar it went on, when it went to the cards. */
    get decidedOn() {
      return decidedOn;
    },
    get drawn() {
      return verdict === 'draw';
    },
    /** Seconds left on the round, or 0 when it is not being fought. */
    get remaining() {
      return phase === 'fighting' ? Math.max(0, config.limit - elapsed) : 0;
    },

    /** Ends the round on the bars now, whatever the clock says. */
    callIt() {
      return phase === 'fighting' ? callIt() : null;
    },
    get elapsed() {
      return elapsed;
    },
    /** Seconds left on the intro, or 0 once it is done. */
    get countdown() {
      return phase === 'intro' ? Math.max(0, config.intro - elapsed) : 0;
    },
    /** True once the result has been up long enough to clear it. */
    get settled() {
      return phase === 'over' && elapsed >= config.celebrate;
    },

    /** Whether the marks are being held by something outside the match. */
    get held() {
      return held;
    },
    /**
     * Holds the fighters on their marks, or lets them go.
     *
     * Only meaningful during the intro. Releasing does not start the round by
     * itself — the intro clock finishes it — so a screen that comes down early
     * does not skip the square-up.
     */
    hold(next = true) {
      held = Boolean(next);
      return held;
    },

    /**
     * Skips the rest of the intro, hold and all.
     *
     * The hold is cleared rather than respected: this is the explicit "go"
     * door, and a headless run that has no screens to dismiss uses it to get
     * past one that is being held by something it cannot see.
     */
    start() {
      held = false;
      if (phase === 'intro') enter('fighting');
      return phase;
    },

    /**
     * Back to the marks: both fighters restored, the meter emptied, the round
     * starting again from the intro. The only way out of `over`.
     */
    reset() {
      fighters.forEach((fighter, i) => restore(fighter, marks[i]));
      winner = null;
      loser = null;
      finishedBy = null;
      verdict = null;
      decidedOn = null;
      // Forced rather than entered, so a reset from `intro` still starts its
      // clock over rather than being swallowed as a no-op.
      const from = phase;
      phase = 'intro';
      elapsed = 0;
      // A hold belongs to the round it was put on. Carrying one across a reset
      // is how a round starts that nobody can ever begin.
      held = false;
      for (const listener of listeners) listener({ from, to: 'intro', winner, loser });
      return phase;
    },

    update(dt) {
      if (!(dt > 0)) return phase;
      elapsed += dt;
      if (phase === 'intro' && !held && elapsed >= config.intro) enter('fighting');
      // And the bell. A fight nobody finishes is decided rather than left
      // running: see `decision.js` for what the bars are read in.
      if (phase === 'fighting' && elapsed >= config.limit) callIt();
      return phase;
    },

    onPhase(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    dispose() {
      stop?.();
      listeners.clear();
    },
  };
}
